import { ArrowUpRight, ImageIcon, MonitorSmartphone, RectangleVertical } from 'lucide-react'
import { Badge, ButtonLink, Card } from '@/components/play'
import { SitePage } from '@/components/SitePage'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

interface Spec {
  field: 'logo' | 'banner_mobile' | 'banner_pillar'
  label: string
  sublabel: string
  pixels: string
  ratio: string
  formats: string
  maxSize: string
  behaviour: string
  composition: string
  icon: typeof ImageIcon
  preview: {
    aspectClass: string
    src?: string
    objectFit?: 'cover' | 'contain'
    objectPosition?: string
    pad?: string
  }
}

const SPECS: Spec[] = [
  {
    field: 'logo',
    label: 'Logo',
    sublabel: 'Map-marker lockup',
    pixels: '480 × 160',
    ratio: '3:1 horizontal',
    formats: 'PNG or JPG · transparent strongly preferred',
    maxSize: '1 MB · sRGB',
    behaviour: 'Anchored to map markers and the sponsor bar. Never crops — always letterboxes inside the 3:1 frame.',
    composition: 'Horizontal wordmark + symbol works best. Square logos waste two-thirds of the frame. If the brand has a sponsorship lockup with white type on a dark background, use it.',
    icon: ImageIcon,
    preview: {
      aspectClass: 'aspect-[3/1]',
      src: '/sponsor-examples/cellc-logo.png',
      objectFit: 'contain',
    },
  },
  {
    field: 'banner_mobile',
    label: 'Banner — mobile',
    sublabel: 'Flush-bottom strip',
    pixels: '1280 × 200',
    ratio: '16:2.5 (6.4:1)',
    formats: 'PNG, JPG, or WebP',
    maxSize: '5 MB · sRGB',
    behaviour: 'Sits flush at the bottom of the portrait immersive view on mobile. Rendered with object-cover object-left — the right edge can crop on narrow phones.',
    composition: 'Anchor logo and key copy in the left third. Treat the right side as expendable. One promise, one wordmark, one accent colour. Two-line headlines lose.',
    icon: MonitorSmartphone,
    preview: {
      aspectClass: 'aspect-[1290/435]',
      src: '/sponsor-examples/cellc-banner.webp',
      objectFit: 'contain',
    },
  },
  {
    field: 'banner_pillar',
    label: 'Pillar — immersive',
    sublabel: 'Right-rail poster',
    pixels: '1080 × 1920',
    ratio: '9:16 portrait',
    formats: 'PNG, JPG, or WebP',
    maxSize: '5 MB · sRGB',
    behaviour: 'Portrait creative flush against the right edge of the immersive video. Contained, not cropped — design edge-to-edge.',
    composition: 'Treat it as a poster, not a banner ad. One promise headline in the upper third, hero photography below, partnership stamp in a corner, CTA host baked into the bottom strip. No "BUY NOW" pills.',
    icon: RectangleVertical,
    preview: {
      aspectClass: 'aspect-[9/16]',
      src: '/sponsor-examples/cellc-pillar.webp',
      objectFit: 'cover',
    },
  },
]

export function SponsorSpecsPage() {
  useDocumentTitle('Sponsor brief · StreamBridge')
  return (
    <SitePage mainId="sponsor-brief-main">
      <section className="relative overflow-hidden px-6 pt-24 pb-12">
        <div className="relative mx-auto max-w-4xl">
          <div className="mb-m">
            <Badge>Sponsor brief</Badge>
          </div>

          <h1 className="text-4xl font-black text-neutral-10 sm:text-5xl md:text-6xl">
            Three creatives. One immersive view.
          </h1>

          <p className="mt-l max-w-2xl text-lg text-neutral-7">
            A horizontal logo lockup, a flush-bottom mobile banner, and a portrait pillar that runs alongside live video. Spec, behaviour, and a production reference for each — Cell C × Comrades 2026.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto flex max-w-4xl flex-col gap-7">
          {SPECS.map((spec) => (
            <SpecCard key={spec.field} spec={spec} />
          ))}
        </div>

        <Card title="Across all three" className="mx-auto mt-12 max-w-4xl">
          <ul className="grid gap-x-8 gap-y-1.5 text-neutral-7 sm:grid-cols-2">
            <li>Static only — no GIF, no WebM, no autoplay</li>
            <li>sRGB colour space, no embedded profiles</li>
            <li>Bake any CTA host into the artwork itself</li>
            <li>The whole surface is the click-through — no buttons</li>
          </ul>
        </Card>

        <Card
          title="Ready to upload?"
          description="Add the three images to a campaign in your event's Sponsors view."
          actions={
            <ButtonLink to="/app">
              Open your events
              <ArrowUpRight aria-hidden="true" />
            </ButtonLink>
          }
          className="mx-auto mt-10 max-w-4xl"
        />
      </section>
    </SitePage>
  )
}

function SpecCard({ spec }: { spec: Spec }) {
  const Icon = spec.icon
  return (
    <Card
      as="article"
      title={spec.label}
      description={spec.sublabel}
      icon={<Icon />}
    >
      <div className="grid gap-7 md:grid-cols-[1fr_1.05fr]">
        <div className="flex flex-col">
          <div className="inline-flex w-fit items-baseline gap-3 rounded-full bg-color-1-transparent px-4 py-1.5 text-sm font-semibold text-neutral-10">
            <span className="font-mono">{spec.pixels}</span>
            <span className="font-normal text-neutral-7">· {spec.ratio}</span>
          </div>

          <dl className="mt-5 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-neutral-7">Format</dt>
            <dd className="text-neutral-8">{spec.formats}</dd>
            <dt className="text-neutral-7">Max</dt>
            <dd className="text-neutral-8">{spec.maxSize}</dd>
            <dt className="text-neutral-7">Behaviour</dt>
            <dd className="text-neutral-8">{spec.behaviour}</dd>
            <dt className="text-neutral-7">Composition</dt>
            <dd className="text-neutral-8">{spec.composition}</dd>
          </dl>
        </div>

        <div className="flex items-center justify-center">
          <div
            className={`relative w-full overflow-hidden rounded-xl bg-neutral-10 ${spec.preview.aspectClass} ${
              spec.field === 'banner_pillar' ? 'max-w-[280px]' : ''
            }`}
          >
            {spec.preview.src ? (
              <img
                src={spec.preview.src}
                alt={`${spec.label} example — Cell C × Comrades 2026`}
                className="absolute inset-0 block h-full w-full"
                style={{
                  objectFit: spec.preview.objectFit ?? 'cover',
                  objectPosition: spec.preview.objectPosition ?? 'center',
                  padding: spec.preview.pad ?? 0,
                }}
                loading="lazy"
              />
            ) : null}
            <Badge tone="overlay" className="pointer-events-none absolute bottom-xs right-xs z-10">
              Cell C × Comrades 2026
            </Badge>
          </div>
        </div>
      </div>
    </Card>
  )
}
